
import { useState, useEffect, useCallback, useRef } from "react";
import { SubscriptionService, SubscriptionStatus } from "@/services/subscription";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate, useLocation } from "react-router-dom";

export const useSubscription = () => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(0);
  const [refreshCount, setRefreshCount] = useState(0);
  const isRefreshingRef = useRef(false); // Use ref to track refresh state without re-renders
  const initialAuthCheckDoneRef = useRef(false);
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const loadSubscriptionStatus = async (userId: string): Promise<SubscriptionStatus | null> => {
    // Prevent concurrent refreshes
    if (isRefreshingRef.current) {
      console.log('Already refreshing subscription, skipping duplicate call');
      return subscriptionStatus;
    }
    
    setIsLoading(true);
    isRefreshingRef.current = true;
    
    try {
      console.log('Loading subscription status for user:', userId, 'refresh count:', refreshCount);
      const status = await SubscriptionService.getSubscriptionStatus(userId);
      console.log('Loaded subscription status:', status);
      setSubscriptionStatus(status);
      return status;
    } catch (error) {
      console.error('Error loading subscription status:', error);
      return null;
    } finally {
      setIsLoading(false);
      isRefreshingRef.current = false;
    }
  };

  // Function to force a refresh, useful when subscription status might have changed
  const forceRefresh = useCallback(() => {
    if (isRefreshingRef.current) {
      console.log('Already refreshing, skipping duplicate refresh request');
      return;
    }
    
    console.log('Force refreshing subscription status');
    setRefreshCount(prev => prev + 1);
    setLastRefresh(Date.now());
    
    // Show a toast to indicate we're refreshing
    toast({
      title: "Refreshing",
      description: "Checking subscription status...",
      duration: 3000,
    });
  }, [toast]);

  // Check URL for checkout success parameter only once on component mount
  useEffect(() => {
    const checkForCheckoutSuccess = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get('success');
      
      if (success === 'true' && user) {
        console.log('Detected successful checkout in URL, setting activation flags');
        localStorage.setItem('subscription_activated', 'true');
        localStorage.setItem('subscription_activated_time', Date.now().toString());
        
        // Immediately refresh to reflect changes
        if (!isRefreshingRef.current) {
          forceRefresh();
        }
        
        // After a while, check if the subscription is active
        setTimeout(async () => {
          if (!isRefreshingRef.current) {
            const status = await loadSubscriptionStatus(user.id);
            if (status && status.isActive) {
              console.log('Subscription confirmed as active after delayed check');
              // Clear the URL params
              navigate(location.pathname, { replace: true });
            } else {
              console.log('Subscription not yet active after delayed check, will try again');
              // Try one more time
              setTimeout(() => {
                if (!isRefreshingRef.current) forceRefresh();
              }, 5000);
            }
          }
        }, 3000);
      }
    };
    
    // Only run once on mount
    checkForCheckoutSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    // Don't run if we're already refreshing to prevent concurrent refresh operations
    if (isRefreshingRef.current) return;
    
    // Check if we just completed checkout and need to refresh
    const justActivated = localStorage.getItem('subscription_activated');
    const activatedTime = localStorage.getItem('subscription_activated_time');
    
    // Force a status refresh when the component mounts if there's a successful checkout flag
    if (justActivated && activatedTime && !isRefreshingRef.current) {
      // We want a slight delay to ensure Stripe webhook has processed
      const activationAge = Date.now() - parseInt(activatedTime || '0');
      if (activationAge < 300000) { // Within 5 minutes
        console.log('Recent activation detected, will refresh subscription status');
        forceRefresh();
      }
    }
  }, [forceRefresh]);

  useEffect(() => {
    // Skip if we're already refreshing to prevent race conditions
    if (isRefreshingRef.current) return;
    
    const refreshSubscription = async (session: any) => {
      if (!session?.user) {
        setIsLoading(false);
        return;
      }
      
      if (isRefreshingRef.current) {
        console.log('Already refreshing, skipping auth-triggered refresh');
        return;
      }
      
      console.log('Refreshing subscription for user:', session.user.id, 'refresh count:', refreshCount);
      
      // Check if we just completed checkout and need to refresh
      const justActivated = localStorage.getItem('subscription_activated');
      
      if (justActivated) {
        // Add a delay to ensure Stripe webhook has processed
        console.log('Activation flag detected, adding delay before checking status');
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
      
      await loadSubscriptionStatus(session.user.id);
      initialAuthCheckDoneRef.current = true;
    };

    // Get initial session - only run this once
    if (!initialAuthCheckDoneRef.current) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        console.log('Auth session check:', session?.user?.id);
        setUser(session?.user ?? null);
        if (session?.user && !isRefreshingRef.current) {
          refreshSubscription(session);
        } else {
          setIsLoading(false);
          initialAuthCheckDoneRef.current = true;
        }
      });
    }

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('Auth state change:', session?.user?.id);
      const newUser = session?.user ?? null;
      
      // Only trigger refresh if user changed
      if (JSON.stringify(newUser) !== JSON.stringify(user)) {
        setUser(newUser);
        if (newUser && !isRefreshingRef.current) {
          refreshSubscription(session);
        } else if (!newUser) {
          setIsLoading(false);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [lastRefresh, refreshCount, user]); 

  return {
    user,
    subscriptionStatus,
    loadSubscriptionStatus,
    forceRefresh,
    isLoading
  };
};
